import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import RichTextEditor from "./RichTextEditor";
import ResizableVarsPanel from "./ResizableVarsPanel";
import { LetterA4Preview } from "./LetterA4Preview";
import { DEFAULT_COPERTINA_TEMPLATE } from "../lib/copertina";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { FileText, Folder, FolderOpen, ChevronDown, ChevronRight, Save, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

// Editor della copertina pratica: editor ricco con variabili, anteprima dati esempio e salvataggio
// del template nel database (settings.copertina_template). Stesse funzionalità dell'editor lettere.
export default function CopertinaEditor({ templateValue, onChange, onSaveTemplate } = {}) {
  const controlled = templateValue !== undefined;
  const [content, setContent] = useState(controlled ? ((templateValue && templateValue.trim()) ? templateValue : DEFAULT_COPERTINA_TEMPLATE) : "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [varCategories, setVarCategories] = useState([]);
  const [varOpen, setVarOpen] = useState({});
  const [previewOn, setPreviewOn] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [margins, setMargins] = useState({ top: 20, right: 15, bottom: 15, left: 20 });
  const [footer, setFooter] = useState(true);
  const editorRef = useRef(null);

  const toggleCat = (t) => setVarOpen((s) => ({ ...s, [t]: !s[t] }));
  const insertVar = (key, fmt) => {
    const token = "{{" + key + (fmt ? ":" + fmt : "") + "}}";
    editorRef.current?.chain().focus().insertContent(token).run();
  };

  const varRow = (v) => (
    <div key={v.key} className="flex items-start justify-between gap-1" data-testid={`cop-var-row-${v.key}`}>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-foreground font-medium truncate" title={v.label}>{v.label}</div>
        {v.value ? <div className="text-[12px] text-amber-600 font-mono truncate" title={v.value}>{v.value}</div> : null}
      </div>
      <div className="flex gap-0.5 shrink-0 pt-0.5">
        <button type="button" onClick={() => insertVar(v.key, "upper")} data-testid={`cop-var-${v.key}-upper`} title="TUTTO MAIUSCOLO" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">AA</button>
        <button type="button" onClick={() => insertVar(v.key, "title")} data-testid={`cop-var-${v.key}-title`} title="Prima lettera maiuscola" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Aa</button>
        <button type="button" onClick={() => insertVar(v.key, "lower")} data-testid={`cop-var-${v.key}-lower`} title="tutto minuscolo" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">aa</button>
        <button type="button" onClick={() => insertVar(v.key, "titlecase")} data-testid={`cop-var-${v.key}-titlecase`} title="Nome e Cognome (Title Case)" className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">Nc</button>
      </div>
    </div>
  );

  const COP_EXTRA_CAT = { title: "Copertina", vars: [{ key: "copertina.data_pec", label: "Data PEC (chiesta alla stampa)", value: "{{copertina.data_pec}}" }] };
  useEffect(() => {
    api.get("/studio/letter-variables").then(({ data }) => setVarCategories([COP_EXTRA_CAT, ...(data.categories || [])])).catch(() => setVarCategories([COP_EXTRA_CAT]));
    api.get("/studio/me").then(({ data }) => {
      const st = data?.settings || {};
      if (!controlled) setContent((st.copertina_template && st.copertina_template.trim()) ? st.copertina_template : DEFAULT_COPERTINA_TEMPLATE);
      const pm = st.print_margins;
      if (pm && typeof pm.top === "number") setMargins({ top: pm.top, right: pm.right, bottom: pm.bottom, left: pm.left });
      setFooter(st.pdf_footer !== false);
    }).catch(() => { if (!controlled) setContent(DEFAULT_COPERTINA_TEMPLATE); }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!previewOn) return;
    const t = setTimeout(() => {
      api.post("/studio/letter-templates/preview-sample", { content: content || "" })
        .then(({ data }) => setPreviewHtml(data.content || "")).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [previewOn, content]);

  const save = async () => {
    setSaving(true);
    try {
      if (onSaveTemplate) await onSaveTemplate(content);
      else await api.patch("/studio/settings", { copertina_template: content });
      toast.success("Template copertina salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const resetDefault = () => {
    setContent(DEFAULT_COPERTINA_TEMPLATE);
    editorRef.current?.commands?.setContent(DEFAULT_COPERTINA_TEMPLATE);
    toast.info("Template ripristinato (ricorda di salvare)");
  };

  return (
    <div id="sez-editor-copertina" className="pgp-card p-6 mt-6 pgp-fade-up" data-testid="copertina-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-head font-semibold text-foreground flex items-center gap-2"><FileText className="h-4 w-4 text-amber-400" /> Editor Copertina Documenti</h3>
          <p className="text-sm text-muted-foreground mt-1">Personalizza il testo della copertina con l'editor ricco e le variabili. Inserisci <span className="font-mono text-amber-600">{"{{copertina.data_pec}}"}</span> (categoria "Copertina") dove vuoi la data PEC: verrà chiesta alla stampa e, se lasciata vuota, sostituita con un trattino (—). La copertina stampata userà questo template.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button type="button" variant="outline" onClick={resetDefault} data-testid="cop-tpl-reset" className="border-slate-700 text-slate-200 hover:bg-slate-800"><RotateCcw className="h-4 w-4 mr-1" /> Default</Button>
          <Button type="button" onClick={save} disabled={saving || loading} data-testid="cop-tpl-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
      ) : (
        <div className="flex gap-4 mt-4">
          <ResizableVarsPanel storageKey="pgp_cop_vars_w" testId="cop-vars-panel" defaultWidth={300} className="max-h-[56vh] overflow-y-auto pgp-card p-3 space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-amber-500 font-mono font-bold mb-1">Variabili</div>
            <p className="text-[10px] text-muted-foreground mb-2 leading-snug">Apri una categoria e clicca un formato per inserire la variabile nel punto del cursore.</p>
            {varCategories.length === 0 ? (
              <div className="text-[11px] text-muted-foreground">Nessuna variabile disponibile.</div>
            ) : varCategories.map((cat) => {
              const open = !!varOpen[cat.title];
              return (
                <div key={cat.title} className="rounded-lg border border-border bg-card overflow-hidden shadow-sm">
                  <button type="button" onClick={() => toggleCat(cat.title)} data-testid={`cop-cat-${cat.title}`}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-muted hover:bg-accent transition-colors">
                    {open ? <FolderOpen className="h-4 w-4 text-amber-400 shrink-0" /> : <Folder className="h-4 w-4 text-amber-400/70 shrink-0" />}
                    <span className="text-[13px] font-semibold text-foreground flex-1 truncate">{cat.title}</span>
                    <span className="text-[9px] text-muted-foreground">{cat.subcats ? cat.subcats.length : (cat.vars || []).length}</span>
                    {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                  </button>
                  {open && (
                    <div className="px-2 py-2 space-y-1.5 border-t border-border bg-popover">
                      {cat.subcats ? cat.subcats.map((sub) => {
                        const sk = cat.title + "/" + sub.title;
                        const sopen = !!varOpen[sk];
                        return (
                          <div key={sk} className="rounded-md border border-border/60 overflow-hidden">
                            <button type="button" onClick={() => toggleCat(sk)} data-testid={`cop-subcat-${sub.title}`}
                              className="w-full flex items-center gap-2 px-2.5 py-2 text-left bg-muted/60 hover:bg-accent transition-colors">
                              {sopen ? <FolderOpen className="h-3.5 w-3.5 text-amber-400 shrink-0" /> : <Folder className="h-3.5 w-3.5 text-amber-400/70 shrink-0" />}
                              <span className="text-[12px] font-semibold text-foreground flex-1 truncate">{sub.title}</span>
                              <span className="text-[9px] text-muted-foreground">{(sub.vars || []).length}</span>
                              {sopen ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
                            </button>
                            {sopen && (
                              <div className="px-2 py-1.5 space-y-1.5 border-t border-border bg-popover">
                                {(sub.vars || []).map((v) => varRow(v))}
                              </div>
                            )}
                          </div>
                        );
                      }) : (cat.vars || []).map((v) => varRow(v))}
                    </div>
                  )}
                </div>
              );
            })}
          </ResizableVarsPanel>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-foreground block">Contenuto copertina</Label>
              <button type="button" onClick={() => setPreviewOn((v) => !v)} data-testid="cop-preview-toggle"
                className="text-xs font-semibold text-amber-500 hover:text-amber-400">{previewOn ? "◀ Torna all'editor" : "Anteprima con dati esempio ▶"}</button>
            </div>
            {previewOn ? (
              <div className="rounded-md border border-slate-700 bg-slate-300/70 p-3 min-h-[320px] max-h-[460px] overflow-auto shadow-inner" data-testid="cop-preview-panel">
                <LetterA4Preview contentHtml={previewHtml || ""} withHead={false} inte={null} margins={margins} footer={footer} />
              </div>
            ) : (
              <RichTextEditor value={content} onChange={(v) => { setContent(v); onChange && onChange(v); }} onEditorReady={(ed) => { editorRef.current = ed; }} onMarginsChange={setMargins} initialMargins={margins} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
